import {
  AnyFeedItemEntityVersion,
  AnyGalleryEntityVersion,
  Cached,
  Collections,
  FeedCommonDefaultProvider,
  GalleryCommonDefaultProvider,
} from '@uploadcam/common';
import { CollectionReference, Firestore } from 'firebase/firestore';
import { Functions } from 'firebase/functions';
import { getCollectionRef } from '../../@harmony/angular';
import { AuthProvider } from '../auth/auth.provider';
import { CommonProvider } from '../common/common.provider';
import { AddItemsToGalleryInteractor } from './domain/interactors/add-items-to-gallery.interactor';
import { CreateGalleryInteractor } from './domain/interactors/create-gallery.interactor';
import { DeleteGalleryInteractor } from './domain/interactors/delete-gallery.interactor';
import { GetGalleryByIdInteractor } from './domain/interactors/get-gallery-by-id.interactor';
import { GetMirroredFeedItemsInteractor } from './domain/interactors/get-mirrored-feed-items.interactor';
import { GetPublicGalleryInteractor } from './domain/interactors/get-public-gallery.interactor';
import { GetTeamGalleriesInteractor } from './domain/interactors/get-team-galleries.interactor';
import { MirrorFeedItemInteractor } from './domain/interactors/mirror-feed-item.interactor';
import { MirrorGalleryItemsInteractor } from './domain/interactors/mirror-gallery-items.interactor';
import { RemoveItemsFromGalleryInteractor } from './domain/interactors/remove-items-from-gallery.interactor';
import { UpdateGalleryInteractor } from './domain/interactors/update-gallery.interactor';

export abstract class GalleriesProvider {
  abstract provideAddItemsToGallery(): AddItemsToGalleryInteractor;
  abstract provideCreateGallery(): CreateGalleryInteractor;
  abstract provideDeleteGallery(): DeleteGalleryInteractor;
  abstract provideGetGalleryById(): GetGalleryByIdInteractor;
  abstract provideGetMirroredFeedItems(): GetMirroredFeedItemsInteractor;
  abstract provideGetPublicGallery(): GetPublicGalleryInteractor;
  abstract provideGetTeamGalleries(): GetTeamGalleriesInteractor;
  abstract provideMirrorFeedItem(): MirrorFeedItemInteractor;
  abstract provideMirrorGalleryItems(): MirrorGalleryItemsInteractor;
  abstract provideRemoveItemsFromGallery(): RemoveItemsFromGalleryInteractor;
  abstract provideUpdateGallery(): UpdateGalleryInteractor;
}

export class GalleriesDefaultProvider implements GalleriesProvider {
  private readonly feedCollection: CollectionReference<AnyFeedItemEntityVersion>;
  private readonly feedCommon = new FeedCommonDefaultProvider();
  private readonly galleriesCollection: CollectionReference<AnyGalleryEntityVersion>;
  private readonly galleriesCommon = new GalleryCommonDefaultProvider();

  constructor(
    private readonly authProvider: AuthProvider,
    private readonly common: CommonProvider,
    firestore: Firestore,
    private readonly functions: Functions,
  ) {
    this.feedCollection = getCollectionRef(firestore, Collections.Feed);
    this.galleriesCollection = getCollectionRef(firestore, Collections.Galleries);
  }

  @Cached()
  public provideAddItemsToGallery(): AddItemsToGalleryInteractor {
    return new AddItemsToGalleryInteractor(this.galleriesCollection.firestore);
  }

  @Cached()
  public provideCreateGallery(): CreateGalleryInteractor {
    return new CreateGalleryInteractor(
      this.galleriesCollection.firestore,
      this.galleriesCommon.provideGalleryEntityToGalleryMapper(),
    );
  }

  @Cached()
  public provideDeleteGallery(): DeleteGalleryInteractor {
    return new DeleteGalleryInteractor(this.functions);
  }

  @Cached()
  public provideGetGalleryById(): GetGalleryByIdInteractor {
    return new GetGalleryByIdInteractor(
      this.galleriesCollection,
      this.common.provideLogger().withTag('GetGalleryByIdInteractor'),
      this.galleriesCommon.provideGalleryEntityToGalleryMapper(),
    );
  }

  @Cached()
  public provideGetMirroredFeedItems(): GetMirroredFeedItemsInteractor {
    return new GetMirroredFeedItemsInteractor(
      this.feedCollection,
      this.common.provideLogger().withTag('GetMirroredFeedItemsInteractor'),
      this.feedCommon.provideFeedItemDtoToFeedItemMapper(),
    );
  }

  @Cached()
  public provideGetPublicGallery(): GetPublicGalleryInteractor {
    return new GetPublicGalleryInteractor(this.functions);
  }

  @Cached()
  public provideGetTeamGalleries(): GetTeamGalleriesInteractor {
    return new GetTeamGalleriesInteractor(
      this.galleriesCollection,
      this.common.provideLogger().withTag('GetTeamGalleriesInteractor'),
      this.galleriesCommon.provideGalleryEntityToGalleryMapper(),
    );
  }

  @Cached()
  public provideMirrorFeedItem(): MirrorFeedItemInteractor {
    return new MirrorFeedItemInteractor(this.functions, this.authProvider.provideGetAuthDto());
  }

  @Cached()
  public provideMirrorGalleryItems(): MirrorGalleryItemsInteractor {
    return new MirrorGalleryItemsInteractor(
      this.common.provideLogger().withTag('MirrorGalleryItemsInteractor'),
      this.provideMirrorFeedItem(),
    );
  }

  @Cached()
  public provideRemoveItemsFromGallery(): RemoveItemsFromGalleryInteractor {
    return new RemoveItemsFromGalleryInteractor(this.functions);
  }

  @Cached()
  public provideUpdateGallery(): UpdateGalleryInteractor {
    return new UpdateGalleryInteractor(this.galleriesCollection.firestore);
  }
}
